/* eslint-disable react-hooks/rules-of-hooks */
import MainBlock from "@container/MainBlock";
import MainContainer from "@container/MainContainer";
import SideBlock from "@container/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import type { TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import {
  CustomContext,
  ResponseType,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import ArticleCard from "@src/app/components/Card/ArticleCard";
import { Imu1CompanionAdsInfiniteScroll } from "@src/app/components/Elements/Advertisement/variant/AdsCompanion";
import { displayAfter, hideAfter } from "@src/app/components/helper/utils";
import {
  CreateLoadMoreHandler,
  type CreateLoadMoreHandlerProps,
} from "@src/app/hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@src/app/hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@src/app/hooks/useLoadMore/SetFirstRenderedArticleIds";
import { MoreArticlesRequstData } from "@src/app/types/MoreArticlesApi";
import { type ProcessedArticleData } from "@transformer/useOsResponse";
import { Fragment, useState } from "react";

import ErrorPage from "../ErrorPage";
import NotFound from "../NotFound";
import { fetchMoreStoriesData } from "./Section.server";
import { SectionMetaData } from "./SectionMetaData";

const PAGE_SIZE = 5;
const INITIAL_ARTICLE_COUNT = 5;

export default function Section() {
  const routeContext: CustomContext<
    TRouteWithoutRedirect<ProcessedArticleData[], string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const sectionArticles = dataLoaderResponse.payload;

  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);

  const showLoadMore: boolean = sectionArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(sectionArticles, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? sectionArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequstData = {
      filterArticlesBasedOn: sectionArticles[0].sectionName,
      sort: lastLoadedArticle && lastLoadedArticle.sort,
    };

    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    return response;
  };

  let sectionName = "";
  let sectionTamilName = "";
  if (sectionArticles[0].sectionName) {
    sectionName = sectionArticles[0].sectionName;
  }
  if (sectionArticles[0].sections !== undefined) {
    sectionTamilName = sectionArticles[0].sections[0].name;
  }

  const { gaData, metaTags } = SectionMetaData({
    sectionName,
    sectionTamilName,
  });

  return (
    <MainContainer
      gaData={gaData}
      metaTagsProps={metaTags}
      sectionName={sectionName}
    >
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock pageName={sectionName}>
          {[...sectionArticles, ...loadMoreData].map((section, index) => (
            <Fragment key={section.id}>
              {displayAfter(index, PAGE_SIZE) && (
                <Imu1CompanionAdsInfiniteScroll
                  sectionName={sectionName}
                  index={index / PAGE_SIZE}
                />
              )}
              <ArticleCard
                displayWidth={260}
                displayHeight={192}
                elements={{ hasDate: true }}
                {...section}
                overrideStyle={{
                  cardWrapper:
                    (displayAfter(index + 1, PAGE_SIZE)
                      ? "border-none"
                      : "flex-col lg:flex-row") +
                    (hideAfter(index, PAGE_SIZE)
                      ? " lg:flex-row flex-row-reverse"
                      : ""),
                  titleStyle: hideAfter(index, PAGE_SIZE)
                    ? "font-semibold text-2sm text-gray-100 hover:text-gray-100 lg:text-2lg lg:text-red-100 lg:hover:text-red-200"
                    : "text-md lg:text-2lg",
                  paragraphStyle: hideAfter(index, PAGE_SIZE)
                    ? "hidden lg:block"
                    : "",
                  imageWrapperStyle: hideAfter(index, PAGE_SIZE)
                    ? "w-[90px] h-[90px] lg:h-auto"
                    : "",
                  imageStyle: hideAfter(index, PAGE_SIZE)
                    ? "h-[90px] lg:h-auto"
                    : "",
                }}
              />
            </Fragment>
          ))}
          {showLoadMore ? (
            <div className="flex w-full justify-start mb-xl">
              <LoadMore
                rootClassName="my-4 w-full"
                onLoadMore={handleLoadMore}
                hasMore={hasMoreStories}
                loadText="Load More"
              />
            </div>
          ) : null}
        </MainBlock>
        <SideBlock sectionName={sectionName} className="w-full lg:block" />
      </WrapperBlock>
    </MainContainer>
  );
}
